import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { toast } from '@spartan-ng/brain/sonner';
import { environment } from 'environments/environment';
import { firstValueFrom, from, Observable } from 'rxjs';
import {
  getAccountId,
  getActorAccountId,
  isTokenExpired,
  readCredentials,
  toOAuthTokens,
} from './authentication';
import type { OAuthTokenResponse } from './authentication.types';
import { CurrentAccountService } from './current-account.service';
import { TokenStore } from './token-store.service';

/** Web Lock that serializes refresh grants across every tab of the same origin. */
const REFRESH_LOCK_NAME = 'aurora-auth-refresh';

/** What the coordinator tells a caller about an operation it reported a failure for. */
export type RecoveryOutcome = 'retry' | 'terminate';

/**
 * How a refresh attempt ended. `reloading` means a sibling tab switched this tab's identity: the tab
 * is being reloaded to rehydrate from storage, so the caller must neither retry nor sign out.
 */
type RefreshResult = 'refreshed' | 'failed' | 'reloading';

/**
 * The single owner of "a credential is no longer usable" (design.md Decision 1/2,
 * redesign-session-expiry-recovery). It owns exactly three things:
 *
 * (a) THE SINGLE FLIGHT — one pending operation per recovery KIND (refresh grant, impersonation
 *     unwind, terminal sign-out), shared by every caller. The latch is the PENDING PROMISE a later
 *     caller awaits, never a boolean reset on completion.
 * (b) THE DECISION — is the credential impersonated; is it expired; is a stash present; is a
 *     refresh token present; does the operation's identity generation still match.
 * (c) THE USER-FACING OUTCOME — exactly one notice per recovery event, on the coordinator's single
 *     exit (inside the flight body), never on a branch — so adding a branch cannot remove it.
 *
 * Owns every token-endpoint call of the SPA (refresh grant and revocations) as plain REST requests
 * through `HttpClient`, never through Apollo: a failed refresh or revocation can therefore never
 * re-enter the Apollo error link's refresh path. It lives on the `authentication` side of the
 * `graphql → authentication` edge; the `graphql` side (the Apollo `ErrorLink`) MUST resolve this
 * service LAZILY through a captured `Injector` so the Apollo client never depends on it at
 * construction (design.md constraint 2).
 */
@Injectable()
export class SessionRecoveryCoordinatorService {
  private readonly tokenStore = inject(TokenStore);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly currentAccount = inject(CurrentAccountService);
  private readonly document = inject(DOCUMENT);

  private refreshFlight: Promise<RefreshResult> | null = null;
  private unwindFlight: Promise<boolean> | null = null;
  private signOutFlight: Promise<void> | null = null;
  /**
   * The identity generation the last "operation not completed" notice was issued for. This outcome
   * has no shared flight of its own (unlike the other three), so the dedup has to live here instead:
   * `identityGeneration` is monotonic, so ONE number is enough to remember "already notified for the
   * CURRENT identity" — no unbounded set needed. Operations stamped with several DIFFERENT stale
   * generations that all surface after the SAME identity change read the SAME current generation,
   * which is why the key is the CURRENT value, not the generation each operation was stamped with.
   */
  private staleNoticeGeneration: number | null = null;
  /** Set once this tab has been told to reload under a sibling tab's identity; never reset. */
  private reloadingTab = false;

  /**
   * Whether this tab is reloading to rehydrate a sibling tab's identity. A guard that sees
   * `check()` resolve `false` must not start a new sign-in while it holds: its navigation could
   * supersede the reload and write this tab's session over the sibling's.
   */
  isReloadingTab(): boolean {
    return this.reloadingTab;
  }

  /**
   * Delegation point for the route guard's `check()`. Reads `TokenStore` live and decides:
   * retry as-is, refresh, unwind, or terminate. NEVER navigates — `check()` runs inside
   * `canActivate`, so the router (not this method) resolves the redirect on a `false`.
   */
  recoverForGuard(): Observable<boolean> {
    return from(this.recoverForGuardAsync());
  }

  private async recoverForGuardAsync(): Promise<boolean> {
    const access = this.tokenStore.accessToken();

    if (!access) {
      // Never authenticated (or already cleared) — not a recovery event, no notice.
      this.tokenStore.clear();
      return false;
    }

    if (this.tokenStore.isImpersonating()) {
      if (!isTokenExpired(access)) return true;

      const restored = await this.runUnwindFlight();
      if (!restored) {
        await this.runSignOutFlight('navigation.Impersonation.NoStashedSessionNotice');
        return false;
      }
      // The restored operator session may ALSO be expired (the common case). Re-entrant by
      // design and provably bounded: `endImpersonation` consumes the single stash slot, so the
      // recursive pass either evaluates a normal session or finds no stash and stops. It joins
      // the SAME shared refresh flight below rather than starting a second one.
      return this.recoverForGuardAsync();
    }

    const refresh = this.tokenStore.refreshToken();
    if (!refresh) {
      this.tokenStore.clear();
      return false;
    }

    if (!isTokenExpired(access)) return true;

    const refreshed = await this.runRefreshFlight();
    if (refreshed === 'refreshed') return true;
    // The tab is reloading under a sibling's identity: signing out would wipe the shared storage.
    if (refreshed === 'reloading') return false;

    await this.runSignOutFlight('navigation.Session.ExpiredNotice');
    return false;
  }

  /**
   * Delegation point for the Apollo `ErrorLink` on an access-expiry 401. `stampedGeneration` is
   * the identity generation `headersLink` stamped on the operation when it was SENT. ALWAYS
   * navigates to `/auth/sign-in` on a terminal sign-out (constraint 4's ErrorLink-side behavior);
   * an impersonation unwind never navigates (the operator stays in the app under their own
   * session).
   */
  recoverForOperation(stampedGeneration: number): Observable<RecoveryOutcome> {
    return from(this.recoverForOperationAsync(stampedGeneration));
  }

  private async recoverForOperationAsync(stampedGeneration: number): Promise<RecoveryOutcome> {
    if (stampedGeneration !== this.tokenStore.identityGeneration()) {
      // The identity this operation was issued under is already gone. Never retry, never unwind
      // the CURRENT (different) identity — that is the mirror-case and the late-failure case
      // (frontend-account-impersonation spec: "not re-sent under a new impersonation" /
      // "a late failure does not unwind a different impersonation").
      this.notifyOperationNotCompleted();
      return 'terminate';
    }

    if (this.tokenStore.isImpersonating()) {
      // Generation matches, so a live read of isImpersonating() is authoritative (Decision 3).
      // This SPECIFIC operation is never retried — it was authored inside the target's session;
      // replaying it under the operator's identity would carry no actor claim and no audit trail.
      const restored = await this.runUnwindFlight();
      if (!restored) {
        await this.runSignOutFlight('navigation.Impersonation.NoStashedSessionNotice');
        void this.router.navigate(['/auth/sign-in']);
      }
      return 'terminate';
    }

    const refreshed = await this.runRefreshFlight();

    // Re-check the generation IMMEDIATELY BEFORE authorizing the re-send: the refresh was a
    // network round trip, and an impersonation can have become active while it was in flight
    // (the re-send window this task closes). This also protects the case where the grant
    // succeeded but was dropped as stale by runRefreshFlight() — either way, this operation
    // must not be retried under an identity it was never issued for.
    if (stampedGeneration !== this.tokenStore.identityGeneration()) {
      this.notifyOperationNotCompleted();
      return 'terminate';
    }

    if (refreshed === 'refreshed') return 'retry';
    if (refreshed === 'reloading') return 'terminate';

    await this.runSignOutFlight('navigation.Session.ExpiredNotice');
    void this.router.navigate(['/auth/sign-in']);
    return 'terminate';
  }

  /**
   * Direct terminal sign-out — for an operation the `ErrorLink` already retried once.
   *
   * `stampedGeneration` is compared against the CURRENT identity generation before touching
   * anything: an operation carries its own identity, and a late failure from an identity that no
   * longer exists must not sign out whatever DIFFERENT, currently valid session is active now (the
   * mirror-case at this call's own boundary).
   */
  terminate(stampedGeneration: number): Observable<RecoveryOutcome> {
    if (stampedGeneration !== this.tokenStore.identityGeneration()) {
      this.notifyOperationNotCompleted();
      return from(Promise.resolve<RecoveryOutcome>('terminate'));
    }

    return from(
      this.runSignOutFlight('navigation.Session.ExpiredNotice').then(() => {
        void this.router.navigate(['/auth/sign-in']);
        return 'terminate' as const;
      }),
    );
  }

  private notifyOperationNotCompleted(): void {
    const current = this.tokenStore.identityGeneration();
    if (this.staleNoticeGeneration === current) return;
    this.staleNoticeGeneration = current;
    toast.info(this.transloco.translate('navigation.Session.OperationNotCompletedNotice'));
  }

  /** Single-flight refresh grant — the ONLY place in the client that can issue one (Decision 1). */
  private runRefreshFlight(): Promise<RefreshResult> {
    this.refreshFlight ??= this.issueRefreshGrant().finally(() => {
      this.refreshFlight = null;
    });
    return this.refreshFlight;
  }

  /**
   * Serializes the grant across tabs. Refresh tokens rotate and the backend revokes the WHOLE
   * family when a rotated token is presented again (reuse), so a tab holding a stale in-memory token
   * would sign every tab out. `navigator.locks` is required (evergreen browsers, secure contexts) —
   * deliberately no fallback lock; the specs install one.
   */
  private issueRefreshGrant(): Promise<RefreshResult> {
    const usedRefreshToken = this.tokenStore.refreshToken();
    if (!usedRefreshToken) return Promise.resolve('failed');

    return navigator.locks.request(REFRESH_LOCK_NAME, () =>
      this.refreshUnderLock(usedRefreshToken),
    );
  }

  /** Runs INSIDE the lock: storage is re-read here because it may have changed while waiting. */
  private refreshUnderLock(usedRefreshToken: string): Promise<RefreshResult> {
    // Compared BEFORE `syncFromStorage()`, which overwrites the in-memory credentials: a sibling tab
    // may have switched the identity (impersonation, another user). Adopting that into this tab would
    // run it as that identity without the operator stash, and its later sign-out would delete the
    // shared origin slot in every tab. Reload instead, so it rehydrates like a new tab.
    const incoming = readCredentials();
    if (incoming && !this.sameIdentityAsMemory(incoming.accessToken)) {
      this.reloadingTab = true;
      this.document.defaultView?.location.reload();
      return Promise.resolve('reloading');
    }

    const stored = this.tokenStore.syncFromStorage();
    if (!stored) return Promise.resolve('failed');

    // Another tab already rotated the token this tab's request used: the synced pair is the current
    // one, so never present the stale token (that would be reuse). Adopt it, unless its access token
    // is already expired too — then the retry would 401 again, so refresh with the STORED token.
    if (stored.refreshToken !== usedRefreshToken) {
      if (!isTokenExpired(stored.accessToken)) return Promise.resolve('refreshed');
      if (!stored.refreshToken) return Promise.resolve('failed');
      return this.requestRefreshGrant(stored.refreshToken);
    }

    // Storage equals memory here: `usedRefreshToken` IS the stored (current) token.
    return this.requestRefreshGrant(usedRefreshToken);
  }

  private sameIdentityAsMemory(accessToken: string): boolean {
    const current = this.tokenStore.accessToken();
    return (
      getAccountId(current) === getAccountId(accessToken) &&
      getActorAccountId(current) === getActorAccountId(accessToken)
    );
  }

  private requestRefreshGrant(refreshToken: string): Promise<RefreshResult> {
    const launchGeneration = this.tokenStore.identityGeneration();

    return firstValueFrom(
      // Public client (RFC 6749 §2.1): `client_id` in the body, no Authorization header. A plain
      // REST call, so an `invalid_grant` here is handled below and never reaches a global toast.
      this.http.post<OAuthTokenResponse>(
        `${environment.api.rest}/o-auth/token`,
        this.form({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: environment.oAuth.applicationCode,
        }),
      ),
    ).then(
      (response): RefreshResult => {
        const next = toOAuthTokens(response);
        // The identity may have changed WHILE this round trip was in flight (a new impersonation
        // started). Installing a rotated credential for an identity that is no longer current
        // would silently overwrite it — never apply a stale grant. The grant already COMPLETED
        // server-side, though: the backend revoked the OLD pair and issued `next`, so discarding it
        // silently would leave a valid, unrevoked refresh token nobody will ever present again —
        // revoke it best-effort instead of just dropping it on the floor.
        if (this.tokenStore.identityGeneration() !== launchGeneration) {
          if (next.refreshToken) this.revokeRefreshToken(next.refreshToken);
          return 'failed';
        }
        this.tokenStore.setCredentials(next);
        return 'refreshed';
      },
      (): RefreshResult => 'failed', // refresh token rejected (invalid_grant) or network failure
    );
  }

  /** Single-flight impersonation unwind. Restores the stash if present; notifies exactly once. */
  private runUnwindFlight(): Promise<boolean> {
    this.unwindFlight ??= Promise.resolve()
      .then(() => {
        if (!this.tokenStore.hasStashedSession()) return false;
        this.tokenStore.endImpersonation();
        // Re-materialize session-scoped state via an EXPLICIT reload: a same-tick clear-then-set
        // produces no observable edge for CurrentAccountService's consumers to react to.
        this.currentAccount.load();
        toast.info(this.transloco.translate('navigation.Impersonation.ExpiredNotice'));
        return true;
      })
      .finally(() => {
        this.unwindFlight = null;
      });
    return this.unwindFlight;
  }

  /** Single-flight terminal sign-out. Revokes server-side, clears credentials and shows ONE notice;
   * never navigates itself — navigation is each caller's own decision (constraint 4). */
  private runSignOutFlight(noticeKey: string): Promise<void> {
    // The body is DEFERRED onto a microtask so the latch is installed before any of it runs.
    // Calling an async method directly would execute its body synchronously up to the first
    // `await` — and with no refresh token to revoke there is no await at all, so the notice would
    // fire while the assignment was still being evaluated and every later caller would run its own.
    this.signOutFlight ??= Promise.resolve()
      .then(() => this.revokeThenClear(noticeKey))
      .finally(() => {
        this.signOutFlight = null;
      });
    return this.signOutFlight;
  }

  /**
   * Local end of the session, followed by a best-effort server-side revocation (RFC 7009) that is
   * FIRED, never awaited.
   *
   * Unwinds an impersonation stack FIRST, mirroring the adapter's `signOut()` (its comment explains
   * the hazard): during an impersonated session the ACTIVE credential is the impersonation JWT,
   * which has NO refresh token by design, so reading `refreshToken()` off it would silently skip
   * the revocation — and the operator's own token is reachable only through the stash `clear()` is
   * about to delete. The impersonation token itself is revoked as an ACCESS token (RFC 7009), the
   * only way to end it server-side before its expiry.
   *
   * `clear()` and the notice run UNCONDITIONALLY, before any network work: making the local end of
   * the session independent of the network is what keeps this method safe to await from the single
   * sign-out flight even if the revocation itself never settles (the endpoint authenticates the
   * client by `client_id`, so it succeeds with no user session — clearing first is safe). The
   * coordinator issues the revocation ITSELF rather than through `AuthenticationService`: the
   * adapter delegates `check()` to this service, so a call back into the adapter would close a DI
   * cycle inside `authentication`.
   */
  private revokeThenClear(noticeKey: string): void {
    const impersonationToken = this.tokenStore.isImpersonating()
      ? this.tokenStore.accessToken()
      : '';
    if (this.tokenStore.isImpersonating() && this.tokenStore.hasStashedSession()) {
      this.tokenStore.endImpersonation();
    }

    const refreshToken = this.tokenStore.refreshToken();

    this.tokenStore.clear();
    toast.info(this.transloco.translate(noticeKey));

    if (impersonationToken) {
      this.revokeAccessToken(impersonationToken);
    }
    if (refreshToken) {
      this.revokeRefreshToken(refreshToken);
    }
  }

  /**
   * Best-effort RFC 7009 revocation of an ACCESS token (an impersonation token has no refresh token
   * to revoke through). Fired, never awaited: the local unwind must not depend on the network.
   * An already-expired token is skipped: the backend rejects it anyway, so the request is noise.
   */
  revokeAccessToken(accessToken: string): void {
    if (isTokenExpired(accessToken)) return;
    this.fireRevocation({ token: accessToken, tokenTypeHint: 'access_token' });
  }

  /** Fires a best-effort RFC 7009 revocation WITHOUT awaiting it, swallowing its failure so it can
   * never surface as an unhandled rejection. Shared by the terminal sign-out (finding 4) and a
   * refresh grant dropped as stale (finding 3) — both need the local flow to proceed independently
   * of this network call. */
  private revokeRefreshToken(refreshToken: string): void {
    this.fireRevocation({ token: refreshToken });
  }

  private fireRevocation(payload: { token: string; tokenTypeHint?: string }): void {
    firstValueFrom(this.revoke(payload.token, payload.tokenTypeHint)).catch(() => undefined);
  }

  /**
   * RFC 7009 revocation as a cold request: the caller decides whether to await it (`signOut()`,
   * which must not let the browser abort it by navigating away) or to fire it and move on. A public
   * client identifies itself with `client_id` in the body and sends no Authorization header.
   */
  revoke(token: string, tokenTypeHint?: string): Observable<unknown> {
    return this.http.post(
      `${environment.api.rest}/o-auth/revoke`,
      this.form({
        token,
        ...(tokenTypeHint && { token_type_hint: tokenTypeHint }),
        client_id: environment.oAuth.applicationCode,
      }),
    );
  }

  /** An `HttpParams` body makes `HttpClient` send `application/x-www-form-urlencoded`. */
  private form(fields: Record<string, string>): HttpParams {
    return new HttpParams({ fromObject: fields });
  }
}
